import type { WeekSnapshot } from "./api";

/**
 * A week's attribution as the server last reported it (see `WeekSnapshot`):
 * its last human edit (`editedBy`/`editedAt`, owner or planner) and its last
 * edit by someone other than the owner (`plannerEdit`). All null until such
 * an edit; system writes (creation, migration, plan adoption) leave them
 * untouched. Timestamps are ISO UTC ("2026-10-22T02:40:00.123Z").
 */
export type WeekMeta = Pick<WeekSnapshot, "editedBy" | "editedAt" | "plannerEdit">;

export const EMPTY_WEEK_META: WeekMeta = { editedBy: null, editedAt: null, plannerEdit: null };

const WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"] as const;
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"] as const;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Whole local calendar days from `a` to `b` (DST-safe: rounds the 23/25 h days). */
function calendarDaysBetween(a: Date, b: Date): number {
  const dayA = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const dayB = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((dayB - dayA) / 86_400_000);
}

/**
 * When a week was edited, in Spanish and local time, relative to `now`:
 * "hace un momento" (under a minute, or a clock slightly ahead of ours),
 * "hace 5 min" (under an hour), "hoy, 21:40", "ayer, 21:40",
 * "lun 20, 21:40" (the last week), "20 oct, 21:40" (older),
 * "20 oct 2025, 21:40" (another year).
 */
export function formatEditedAt(at: Date, now: Date): string {
  const elapsedMs = now.getTime() - at.getTime();
  if (elapsedMs < 60_000) return "hace un momento";
  if (elapsedMs < 3_600_000) return `hace ${Math.floor(elapsedMs / 60_000)} min`;

  const time = `${pad2(at.getHours())}:${pad2(at.getMinutes())}`;
  const days = calendarDaysBetween(at, now);
  if (days === 0) return `hoy, ${time}`;
  if (days === 1) return `ayer, ${time}`;
  if (days < 7) return `${WEEKDAYS[at.getDay()]} ${at.getDate()}, ${time}`;
  const date = `${at.getDate()} ${MONTHS[at.getMonth()]}`;
  return at.getFullYear() === now.getFullYear() ? `${date}, ${time}` : `${date} ${at.getFullYear()}, ${time}`;
}

/**
 * "X actualizó tu semana" shows while someone else edited my week after the
 * last edit I acknowledged ("Entendido") on this device. Only an ack hides
 * it: my own later saves don't touch `plannerEdit`.
 */
export function shouldShowEditedBanner(meta: WeekMeta, myUserId: number, ackedAt: string | null): boolean {
  const edit = meta.plannerEdit;
  if (!edit || edit.by.id === myUserId) return false;
  const editedAt = Date.parse(edit.at);
  if (Number.isNaN(editedAt)) return false;
  if (ackedAt === null) return true;
  const acked = Date.parse(ackedAt);
  return Number.isNaN(acked) || acked < editedAt;
}

/** localStorage key holding the `plannerEdit.at` I acknowledged for a week. */
export function editAckKey(userId: number, weekId: string): string {
  return `nutri:ack-edit:${userId}:${weekId}`;
}
